const STORAGE_KEY = 'tongxin_auth';
const SESSION_GUEST_KEY = 'tongxin_is_guest';
const VALID_SOURCE_TYPES = ['vaccine', 'health', 'outpatient'];

const normalizeSourceType = (sourceType) => {
  if (typeof sourceType !== 'string') {
    return null;
  }
  return VALID_SOURCE_TYPES.includes(sourceType) ? sourceType : null;
};

const loadFromStorage = () => {
  let parsed = {};
  try {
    const stored = sessionStorage.getItem(STORAGE_KEY);
    if (stored) {
      parsed = JSON.parse(stored);
    }
  } catch (error) {
    console.error('Failed to load tongxin auth from sessionStorage:', error);
  }

  // 向後相容：若舊版資料還在 localStorage，搬移到 sessionStorage
  if (!parsed || Object.keys(parsed).length === 0) {
    try {
      const legacyStored = localStorage.getItem(STORAGE_KEY);
      if (legacyStored) {
        parsed = JSON.parse(legacyStored);
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify(parsed));
        localStorage.removeItem(STORAGE_KEY);
      }
    } catch (error) {
      console.error('Failed to migrate tongxin auth from localStorage to sessionStorage:', error);
    }
  }

  let isGuest = false;
  try {
    isGuest = sessionStorage.getItem(SESSION_GUEST_KEY) === '1';
  } catch (error) {
    console.error('Failed to load tongxin guest session from sessionStorage:', error);
  }

  return {
    // isGuest 維持於當前分頁 / webview 的 sessionStorage，不做跨 session 持久化
    isGuest: isGuest,
    // 僅存在記憶體，用於判斷是否已經從 exam-selection 進入流程
    flowAlive: false,
    clinicId: parsed.clinicId ?? null,
    clinicCode: parsed.clinicCode ?? null,
    clinicName: parsed.clinicName ?? '',
    memberTermChecked: false,
    sourceType: normalizeSourceType(parsed.sourceType),
  };
};

const saveGuestToSession = (isGuest) => {
  try {
    if (isGuest) {
      sessionStorage.setItem(SESSION_GUEST_KEY, '1');
      return;
    }
    sessionStorage.removeItem(SESSION_GUEST_KEY);
  } catch (error) {
    console.error('Failed to save tongxin guest session to sessionStorage:', error);
  }
};

const clearGuestSession = () => {
  try {
    sessionStorage.removeItem(SESSION_GUEST_KEY);
  } catch (error) {
    console.error('Failed to clear tongxin guest session from sessionStorage:', error);
  }
};

const saveToStorage = (state) => {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify({
      clinicId: state.clinicId,
      clinicCode: state.clinicCode,
      clinicName: state.clinicName,
      sourceType: state.sourceType,
    }));
  } catch (error) {
    console.error('Failed to save tongxin auth to sessionStorage:', error);
  }
};

export default {
  namespaced: true,
  state: loadFromStorage(),
  getters: {
    isGuest(state) {
      return state.isGuest;
    },
    flowAlive(state) {
      return state.flowAlive;
    },
    clinicId(state) {
      return state.clinicId;
    },
    clinicCode(state) {
      return state.clinicCode;
    },
    clinicName(state) {
      return state.clinicName;
    },
    memberTermChecked(state) {
      return state.memberTermChecked;
    },
    sourceType(state) {
      return state.sourceType;
    },
    /**
     * 回傳 tongxin 頁面應使用的 token
     * - guest 模式：不需要 token（回傳 null）
     * - LIFF 模式：使用 liffAuth.token
     */
    resolveToken(state, getters, rootState) {
      if (state.isGuest) {
        return null;
      }
      return rootState.liffAuth?.token || null;
    },
  },
  mutations: {
    SET_GUEST(state, isGuest) {
      state.isGuest = Boolean(isGuest);
      saveGuestToSession(state.isGuest);
      saveToStorage(state);
    },
    SET_FLOW_ALIVE(state, isAlive) {
      state.flowAlive = Boolean(isAlive);
    },
    SET_CLINIC_ID(state, clinicId) {
      state.clinicId = clinicId;
      saveToStorage(state);
    },
    SET_CLINIC_CODE(state, clinicCode) {
      state.clinicCode = clinicCode;
      saveToStorage(state);
    },
    SET_CLINIC_NAME(state, clinicName) {
      state.clinicName = clinicName || '';
      saveToStorage(state);
    },
    SET_MEMBER_TERM_CHECKED(state, checked) {
      state.memberTermChecked = checked;
    },
    SET_SOURCE_TYPE(state, sourceType) {
      state.sourceType = normalizeSourceType(sourceType);
      saveToStorage(state);
    },
    CLEAR(state) {
      state.isGuest = false;
      state.flowAlive = false;
      state.clinicId = null;
      state.clinicCode = null;
      state.clinicName = '';
      state.memberTermChecked = false;
      state.sourceType = null;
      clearGuestSession();
      sessionStorage.removeItem(STORAGE_KEY);
      localStorage.removeItem(STORAGE_KEY);
    },
  },
  actions: {
    setGuest({ commit }, isGuest) {
      commit('SET_GUEST', isGuest);
    },
    setFlowAlive({ commit }, isAlive) {
      commit('SET_FLOW_ALIVE', isAlive);
    },
    setClinicId({ commit }, clinicId) {
      commit('SET_CLINIC_ID', clinicId);
    },
    setClinicCode({ commit }, clinicCode) {
      commit('SET_CLINIC_CODE', clinicCode);
    },
    setClinicName({ commit }, clinicName) {
      commit('SET_CLINIC_NAME', clinicName);
    },
    // line 登入 的 term條款頁面，是否顯示
    setMemberTermChecked({ commit }, checked) {
      commit('SET_MEMBER_TERM_CHECKED', checked);
    },
    setSourceType({ commit }, sourceType) {
      commit('SET_SOURCE_TYPE', sourceType);
    },
    clear({ commit }) {
      commit('CLEAR');
    },
  },
};
